import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FatAndProteinService from '~/api/services/fatAndProtein'
import MilkFixedService from '~/api/services/milkFixedPrice'
import ProfileSettingsService from '~/api/services/profileSettings'
import StockExChangeService from '~/api/services/stockExchange'
import Dialog from '~/components/Dialog'
import notify from '~/components/Notify'
import TwoFaComp from '~/components/TwoFaComp'
import { useAuth } from '~/routes/components/AuthProvider'
import { handleErrors } from '~/utils/helpers'

type SlotType = 'fixedPrice' | 'fatProtein' | 'stockExchange'

interface PlanModalProps {
  openDialog: boolean
  handleClose: (submited?: boolean) => void
  showData: any
  slotType?: SlotType
}

const PlanModal: React.FC<PlanModalProps> = (props) => {
  const { t } = useTranslation()
  const {
    openDialog,
    handleClose,
    showData,
    slotType
  } = props
  const { systemData } = useAuth()
  const tfaPlanning = systemData?.roleSettings?.tfaPlanning === 1
  const tfaType = systemData?.logged?.tfaType
  const id = showData?.id

  const [correctTfaCode, setCorrectTfaCode] = useState(false)
  const [tfaCode, setTfaCode] = useState('')
  const [isBusy, setIsBusy] = useState(false)

  useEffect(() => {
    if (openDialog && tfaPlanning) {
      handleSendCode()
    }
    setTfaCode('')
    setCorrectTfaCode(false)
  }, [openDialog])

  async function handleSendCode() {
    if (tfaType === 'email' || tfaType === 'sms') {
      setIsBusy(true)
      try {
        await ProfileSettingsService.sendCode()
        notify(t('Code sent'), 'success')
      } catch (error) {
        handleErrors(error)
      }
      setIsBusy(false)
    }
  }

  async function handlePublishTradingSlot(data?) {
    setIsBusy(true)
    try {
      switch (slotType) {
        case 'fatProtein':
          await FatAndProteinService.planTradingSlot(id, data)
          notify(t('Fat & protein trading slot published'), 'success');
          break
        case 'fixedPrice':
          await MilkFixedService.planTradingSlot(id, data)
          notify(t('Trading slot published'), 'success')
          break
        case 'stockExchange':
          await StockExChangeService.planTradingSlot(id, data)
          notify(t('Stock exchange published'), 'success');
          break
        default:
          await MilkFixedService.planTradingSlot(id, data)
          notify(t('Trading slot published'), 'success')
          break
      }
      handleClose(true)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  const uniqId = showData?.uniqueId
  const dateFrom = dayjs(showData?.dateFrom).format(('dddd, D MMMM YYYY HH:mm'))
  const dateTo = dayjs(showData?.dateTo).format(('dddd, D MMMM YYYY HH:mm'))

  return <Dialog
    isDialogOpen={openDialog}
    textClose={t('Cancel process')}
    handleClose={() => {
      setTfaCode('')
      handleClose()
    }}
    style={{ width: '60%' }}
    isLoading={isBusy}
    disableSubmit={tfaPlanning ? !correctTfaCode : false}
    textSubmit={t('Plan and publish')}
    handleSubmit={() => handlePublishTradingSlot(tfaPlanning ? { code: tfaCode } : null)}
    textContent={<div style={{ margin: '20px 70px', textAlign: 'center' }}>
      <p>{t('The trading slot {{uniqId}} with start date {{dateFrom}} and end date {{dateTo}} will be published.', { uniqId, dateFrom, dateTo })}
      </p>
      <b>{t('With the confirmation, all farmers within the groups of farmers will be informed by e-mail about the trading slot.')}</b>
      {tfaPlanning && <TwoFaComp
        onChangeTfaCode={(code) => setTfaCode(code)}
        onChangeCorrectTfaCode={(correctTfaCode) => setCorrectTfaCode(correctTfaCode)}
      />}
    </div>}
    textHeader={t('Trading slot {{uniqId}} plan and publish', { uniqId })}
    type={'abort'}
  />
}
export default PlanModal